/**
 * ==========================================================================
 *  Tourplan.jsx — Tagestouren mit Kunden-Zuweisungen
 * ==========================================================================
 *  Zwei Reiter:
 *    1. Aktuelle Touren — Datum >= heute, max. 10 oben.
 *    2. Historie       — alle vergangenen Touren, neueste zuerst.
 *
 *  Klick auf eine Tour-Karte oeffnet die Detailansicht: Kundenliste
 *  sortiert (bestaetigte oben nach Uhrzeit aufsteigend, dann Planung).
 *  Pro Kunde Status-Toggle (Planung/Bestaetigt), Uhrzeit-Feld bei
 *  Bestaetigung, Entfernen-Button.
 *
 *  "Neue Tour anlegen" oeffnet ein Modal mit Name + Datum.
 *  "+ Kunde hinzufuegen" oeffnet eine Suchliste aller Kunden.
 *
 *  Backend-Verzahnung:
 *    GET    /api/touren?modus=aktiv|historie
 *    GET    /api/touren/{id}
 *    POST   /api/touren
 *    DELETE /api/touren/{id}
 *    POST   /api/touren/{id}/kunden
 *    PUT    /api/tour-kunden/{id}
 *    DELETE /api/tour-kunden/{id}
 * ==========================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar as CalendarIcon, Plus, X, Search, Trash2, Clock, Users,
  ChevronLeft, MapPin, Loader2, CheckCircle2, AlertCircle, Smartphone, Edit3,
  ArrowUp, ArrowDown, Route as RouteIcon,
} from 'lucide-react';
import { api, ApiError } from '../api';
import TourRoutenKarte from '../components/TourRoutenKarte';
import { baueRoutenLink, anzahlStoppsMitAdresse } from '../utils/routenLink';
import {
  luftlinienStrecken, strassenStrecken, streckenSignatur, fmtKm, fmtMin,
  berechneTourVorschlag, adresseKoordinaten, TOUR_START,
} from '../utils/tourDistanz';

const CI = { brombeer: '#85366C', orchidee: '#DC91D0', blue: '#2DBAE8', green: '#8CC53F' };

const TABS = [
  { id: 'aktiv', label: 'Aktuelle Touren' },
  { id: 'historie', label: 'Historie' },
];

function formatDatum(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

// Sortiert die Kundenliste einer Tour wie das Backend: bestaetigt zuerst
// (nach Uhrzeit aufsteigend, leere ans Ende), dann die Planungs-Eintraege.
function sortiereTourKunden(kunden) {
  return [...kunden].sort((a, b) => {
    const aB = a.status === 'bestaetigt' ? 0 : 1;
    const bB = b.status === 'bestaetigt' ? 0 : 1;
    if (aB !== bB) return aB - bB;
    if (aB === 0) return (a.uhrzeit || '99:99').localeCompare(b.uhrzeit || '99:99');
    return 0;
  });
}

// Tour-Objekt mit neuer Kundenliste + neu berechneten Zaehlern. Wird fuer
// lokale In-Place-Updates genutzt, damit die Ansicht nach einer Uhrzeit-/
// Status-Aenderung NICHT neu laedt (Karte bleibt, kein Sprung nach oben).
function tourMitKunden(prev, kunden) {
  const sortiert = sortiereTourKunden(kunden);
  const anzahl_bestaetigt = sortiert.filter(k => k.status === 'bestaetigt').length;
  return {
    ...prev,
    kunden: sortiert,
    anzahl_bestaetigt,
    anzahl_geplant: sortiert.length - anzahl_bestaetigt,
  };
}

export default function Tourplan({ initialTourId = null }) {
  const [tab, setTab] = useState('aktiv');
  const [touren, setTouren] = useState([]);
  const [touIsLoading, setTouIsLoading] = useState(true);
  const [touError, setTouError] = useState(null);

  const [selectedTourId, setSelectedTourId] = useState(null);
  const [tourDetail, setTourDetail] = useState(null);
  const [tourDetailLoading, setTourDetailLoading] = useState(false);

  const [alleKunden, setAlleKunden] = useState([]);

  const [showNeueTourModal, setShowNeueTourModal] = useState(false);
  const [showEditTourModal, setShowEditTourModal] = useState(false);
  const [showKundeModal, setShowKundeModal] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  // === Touren laden bei Tab-Wechsel ===
  useEffect(() => {
    let abgebrochen = false;
    setTouIsLoading(true);
    setTouError(null);
    api.get(`/api/touren?modus=${tab}`)
      .then(daten => { if (!abgebrochen) setTouren(daten || []); })
      .catch(e => {
        if (!abgebrochen) setTouError(e?.detail || 'Touren konnten nicht geladen werden.');
      })
      .finally(() => { if (!abgebrochen) setTouIsLoading(false); });
    return () => { abgebrochen = true; };
  }, [tab, refreshKey]);

  // === Tour-Details laden bei Auswahl ===
  useEffect(() => {
    if (!selectedTourId) { setTourDetail(null); return; }
    let abgebrochen = false;
    setTourDetailLoading(true);
    api.get(`/api/touren/${selectedTourId}`)
      .then(d => { if (!abgebrochen) setTourDetail(d); })
      .catch(e => {
        if (!abgebrochen) {
          alert(e?.detail || 'Tour-Details konnten nicht geladen werden.');
          setSelectedTourId(null);
        }
      })
      .finally(() => { if (!abgebrochen) setTourDetailLoading(false); });
    return () => { abgebrochen = true; };
  }, [selectedTourId, refreshKey]);

  // === Kundenliste einmalig laden (fuer das Hinzufuegen-Modal) ===
  useEffect(() => {
    api.get('/api/kunden').then(setAlleKunden).catch(() => setAlleKunden([]));
  }, []);

  // Deep-Link aus der Kundenakte ("Tour öffnen"): vorausgewaehlte Tour
  // direkt in der Detailansicht oeffnen.
  useEffect(() => {
    if (initialTourId) setSelectedTourId(initialTourId);
  }, [initialTourId]);

  const reload = () => setRefreshKey(k => k + 1);

  // === Aktionen ===
  const handleCreateTour = async (name, datum) => {
    try {
      const neu = await api.post('/api/touren', { name, datum });
      setShowNeueTourModal(false);
      reload();
      // direkt in die Detailansicht der frisch angelegten Tour springen
      setTab(new Date(datum) < new Date(new Date().setHours(0,0,0,0)) ? 'historie' : 'aktiv');
      setTimeout(() => setSelectedTourId(neu.id), 0);
    } catch (e) {
      alert(e?.detail || 'Tour konnte nicht angelegt werden.');
    }
  };

  const handleDeleteTour = async (tour) => {
    if (!window.confirm(`Tour „${tour.name}" am ${formatDatum(tour.datum)} wirklich löschen?\n\nAlle ${tour.anzahl_gesamt || 0} zugeordneten Kunden werden auch entfernt.`)) return;
    try {
      await api.del(`/api/touren/${tour.id}`);
      setSelectedTourId(null);
      reload();
    } catch (e) {
      alert(e?.detail || 'Tour konnte nicht gelöscht werden.');
    }
  };

  // Tour bearbeiten: Name und/oder Datum aendern. Wenn das Datum vom
  // aktiven Tab in den Historie-Tab rutscht (oder umgekehrt), bleibt
  // selectedTourId bestehen, aber wir wechseln den Tab automatisch.
  const handleSaveTourEdit = async (tourId, name, datum) => {
    try {
      await api.put(`/api/touren/${tourId}`, { name, datum });
      setShowEditTourModal(false);
      // Detail neu ziehen + Listen-Refresh
      reload();
      const neueDetails = await api.get(`/api/touren/${tourId}`);
      setTourDetail(neueDetails);
      // Tab korrigieren, wenn Datum die Heute-Grenze ueberschritten hat
      const heuteMidnight = new Date(new Date().setHours(0,0,0,0));
      const tourTag = new Date(datum);
      setTab(tourTag < heuteMidnight ? 'historie' : 'aktiv');
    } catch (e) {
      alert(e?.detail || 'Tour konnte nicht gespeichert werden.');
    }
  };

  const handleAddKunde = async (kundeId, status = 'planung') => {
    try {
      await api.post(`/api/touren/${selectedTourId}/kunden`, { kunde_id: kundeId, status });
      setShowKundeModal(false);
      reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        alert(e.detail);
      } else {
        alert(e?.detail || 'Kunde konnte nicht zugewiesen werden.');
      }
    }
  };

  const handleUpdateAssignment = async (tkId, patch) => {
    try {
      await api.put(`/api/tour-kunden/${tkId}`, patch);
      // Lokales In-Place-Update statt reload(): nur den geaenderten Kunden
      // patchen + neu sortieren. Die Karte bleibt stehen, die Ansicht
      // springt NICHT nach oben — Hendrik kann Kunde fuer Kunde editieren.
      setTourDetail(prev => prev
        ? tourMitKunden(prev, (prev.kunden || []).map(k => (k.id === tkId ? { ...k, ...patch } : k)))
        : prev);
    } catch (e) {
      alert(e?.detail || 'Aenderung konnte nicht gespeichert werden.');
    }
  };

  const handleRemoveAssignment = async (tkId, kundeName) => {
    if (!window.confirm(`„${kundeName}" wirklich von der Tour entfernen?`)) return;
    try {
      await api.del(`/api/tour-kunden/${tkId}`);
      // Lokal entfernen statt reload() — kein Sprung nach oben.
      setTourDetail(prev => prev
        ? tourMitKunden(prev, (prev.kunden || []).filter(k => k.id !== tkId))
        : prev);
    } catch (e) {
      alert(e?.detail || 'Entfernen fehlgeschlagen.');
    }
  };

  // === Render ===
  return (
    <div className="bg-slate-100 min-h-[calc(100vh-60px)]">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {!selectedTourId ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <MapPin className="w-6 h-6" style={{ color: CI.brombeer }} />
                  Tourplan
                </h1>
                <p className="text-sm text-gray-500 mt-1">Tagestouren mit Kunden-Zuordnungen — Planung und bestätigte Termine.</p>
              </div>
              <button
                onClick={() => setShowNeueTourModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-white font-bold shadow-sm hover:shadow-md transition-all"
                style={{ backgroundColor: CI.brombeer }}
              >
                <Plus className="w-4 h-4" /> Neue Tour anlegen
              </button>
            </div>

            <div className="flex gap-6 border-b border-gray-200 mb-6">
              {TABS.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`pb-3 text-sm font-bold border-b-2 transition-colors ${tab === t.id ? 'text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  style={{ borderColor: tab === t.id ? CI.brombeer : 'transparent' }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {touIsLoading ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm py-12 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Touren werden geladen…
              </div>
            ) : touError ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-2 text-sm text-red-800">
                <AlertCircle className="w-4 h-4" /> {touError}
              </div>
            ) : touren.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  {tab === 'aktiv' ? 'Noch keine geplanten Touren' : 'Keine Touren in der Historie'}
                </h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  {tab === 'aktiv'
                    ? 'Lege eine erste Tour an — Name + Tagesdatum. Kunden werden im nächsten Schritt zugeordnet.'
                    : 'Sobald geplante Touren in der Vergangenheit liegen, erscheinen sie hier.'}
                </p>
                {tab === 'aktiv' && (
                  <button
                    onClick={() => setShowNeueTourModal(true)}
                    className="mt-6 px-5 py-2.5 rounded-lg text-white font-bold"
                    style={{ backgroundColor: CI.brombeer }}
                  >
                    + Neue Tour anlegen
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {touren.map(t => (
                  <TourKarte key={t.id} tour={t} onClick={() => setSelectedTourId(t.id)} />
                ))}
              </div>
            )}
          </>
        ) : (
          <TourDetailView
            key={tourDetail?.id || 'lade'}
            tour={tourDetail}
            isLoading={tourDetailLoading}
            alleKunden={alleKunden}
            onBack={() => { setSelectedTourId(null); reload(); }}
            onDeleteTour={handleDeleteTour}
            onEditTour={() => setShowEditTourModal(true)}
            onShowKundeModal={() => setShowKundeModal(true)}
            onAddKunde={handleAddKunde}
            onUpdateAssignment={handleUpdateAssignment}
            onRemoveAssignment={handleRemoveAssignment}
          />
        )}
      </div>

      {showNeueTourModal && (
        <TourFormModal
          modus="neu"
          onClose={() => setShowNeueTourModal(false)}
          onSubmit={handleCreateTour}
        />
      )}

      {showEditTourModal && tourDetail && (
        <TourFormModal
          modus="edit"
          initialName={tourDetail.name}
          initialDatum={tourDetail.datum}
          onClose={() => setShowEditTourModal(false)}
          onSubmit={(name, datum) => handleSaveTourEdit(tourDetail.id, name, datum)}
        />
      )}

      {showKundeModal && tourDetail && (
        <KundeAuswahlModal
          alleKunden={alleKunden}
          bereitsZugeordnet={(tourDetail.kunden || []).map(k => k.kunde_id)}
          onClose={() => setShowKundeModal(false)}
          onAdd={handleAddKunde}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Tour-Karte in der Liste
// --------------------------------------------------------------------------
function TourKarte({ tour, onClick }) {
  const datum = formatDatum(tour.datum);
  return (
    <button
      onClick={onClick}
      className="text-left bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md hover:border-fuchsia-200 transition-all"
    >
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
        <CalendarIcon className="w-3.5 h-3.5" />
        {datum}
      </div>
      <h3 className="text-lg font-bold text-gray-800 mb-3">{tour.name}</h3>
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">
          <CheckCircle2 className="w-3 h-3" /> {tour.anzahl_bestaetigt} bestätigt
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200 font-bold">
          <Clock className="w-3 h-3" /> {tour.anzahl_geplant} geplant
        </span>
      </div>
    </button>
  );
}

// --------------------------------------------------------------------------
// Detail-Ansicht einer Tour: Kundenliste + Aktionen
// --------------------------------------------------------------------------
function TourDetailView({
  tour, isLoading, alleKunden, onBack, onDeleteTour, onEditTour,
  onShowKundeModal, onAddKunde, onUpdateAssignment, onRemoveAssignment,
}) {
  // === Entfernungen zwischen den Stopps (in Tour-Reihenfolge) ===
  // Luftlinie sofort (immer da). Strasse via OSRM asynchron nachladen; bei
  // Erfolg ersetzt sie die Anzeige, sonst bleibt es bei Luftlinie.
  const stopps = useMemo(
    () => (tour?.kunden || []).map(tk => ({ id: tk.id, lat: tk.kunde_lat, lng: tk.kunde_lng })),
    [tour?.kunden]
  );
  const sig = useMemo(() => streckenSignatur(stopps), [stopps]);
  const luft = useMemo(() => luftlinienStrecken(stopps), [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  const [strasse, setStrasse] = useState(null);
  const [strasseLaedt, setStrasseLaedt] = useState(false);
  const [strasseFehler, setStrasseFehler] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    // Zaehlt man die Stopps mit Koordinaten? Unter 2 gibt es nichts zu routen.
    const mitKo = stopps.filter(s => typeof s.lat === 'number' && typeof s.lng === 'number');
    setStrasse(null);
    setStrasseFehler(false);
    if (mitKo.length < 2) { setStrasseLaedt(false); return; }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStrasseLaedt(true);
    strassenStrecken(stopps, ctrl.signal)
      .then(r => { if (!ctrl.signal.aborted) { setStrasse(r); setStrasseFehler(false); } })
      .catch(e => {
        if (ctrl.signal.aborted) return;
        console.warn('Strassen-Routing (OSRM) fehlgeschlagen — Luftlinie als Fallback', e);
        setStrasse(null);
        setStrasseFehler(true);
      })
      .finally(() => { if (!ctrl.signal.aborted) setStrasseLaedt(false); });
    return () => ctrl.abort();
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const effektiv = strasse || luft;
  const distanzMap = effektiv.proStopp || {};

  // === Assistierte Reihenfolge-Optimierung ===
  const [vorschlag, setVorschlag] = useState(null);
  const [vorschlagLaedt, setVorschlagLaedt] = useState(false);
  const [vorschlagFehler, setVorschlagFehler] = useState(null);
  // Gemerkte Einstellungen dieser Tour aus dem Tour-Datensatz laden (kommt vom
  // Server, daher auf jedem Geraet gleich). Component wird pro Tour via
  // key={tour.id} neu gemountet, daher reichen die Initialwerte.
  const [startzeit, setStartzeit] = useState(tour?.startzeit || '09:00');
  const [dauerMin, setDauerMin] = useState(tour?.dauer_minuten || 30);
  const [anwendenLaeuft, setAnwendenLaeuft] = useState(false);
  // Start-/Zieladresse: je mit Haekchen "in die Route aufnehmen". Standard =
  // Buero (SPA), beide an. Start kann man weglassen (schon unterwegs), Ziel
  // (z.B. Hotel) bleibt sinnvoll fuers Navi am Ende. Werte werden pro Tour
  // server-seitig gemerkt.
  const [startEinbeziehen, setStartEinbeziehen] = useState(tour?.start_einbeziehen ?? true);
  const [startAdresse, setStartAdresse] = useState(tour?.start_adresse ?? TOUR_START.label);
  const [zielEinbeziehen, setZielEinbeziehen] = useState(tour?.ziel_einbeziehen ?? true);
  const [zielAdresse, setZielAdresse] = useState(tour?.ziel_adresse ?? TOUR_START.label);
  // Geokodierte Anker fuer die Karte (Pins + Linie). Debounced, damit nicht
  // jeder Tastendruck eine Geocoder-Anfrage ausloest.
  const [startPunkt, setStartPunkt] = useState(null);
  const [zielPunkt, setZielPunkt] = useState(null);

  // Einstellungen dieser Tour bei jeder Aenderung server-seitig merken
  // (debounced, damit nicht jeder Tastendruck einen Request ausloest). Der
  // erste Durchlauf nach dem Mount wird uebersprungen — sonst wuerde das
  // blosse Oeffnen der Tour die Werte gleich wieder zurueckschreiben.
  const erstSpeicherRef = useRef(true);
  useEffect(() => {
    if (erstSpeicherRef.current) { erstSpeicherRef.current = false; return; }
    if (!tour?.id) return;
    const tid = setTimeout(() => {
      api.put(`/api/touren/${tour.id}`, {
        startzeit,
        dauer_minuten: dauerMin,
        start_einbeziehen: startEinbeziehen,
        start_adresse: startAdresse,
        ziel_einbeziehen: zielEinbeziehen,
        ziel_adresse: zielAdresse,
      }).catch(e => console.warn('Tour-Einstellungen konnten nicht gespeichert werden', e));
    }, 700);
    return () => clearTimeout(tid);
  }, [tour?.id, startzeit, dauerMin, startEinbeziehen, startAdresse, zielEinbeziehen, zielAdresse]);

  useEffect(() => {
    let aktiv = true;
    const tid = setTimeout(async () => {
      const s = startEinbeziehen ? await adresseKoordinaten((startAdresse || '').trim() || TOUR_START.query) : null;
      if (aktiv) setStartPunkt(s);
    }, 500);
    return () => { aktiv = false; clearTimeout(tid); };
  }, [startEinbeziehen, startAdresse]);

  useEffect(() => {
    let aktiv = true;
    const tid = setTimeout(async () => {
      const z = zielEinbeziehen ? await adresseKoordinaten((zielAdresse || '').trim() || TOUR_START.query) : null;
      if (aktiv) setZielPunkt(z);
    }, 500);
    return () => { aktiv = false; clearTimeout(tid); };
  }, [zielEinbeziehen, zielAdresse]);

  const optimieren = async () => {
    setVorschlagLaedt(true); setVorschlagFehler(null); setVorschlag(null);
    try {
      let start = null;
      if (startEinbeziehen) {
        start = await adresseKoordinaten(startAdresse.trim() || TOUR_START.query);
        if (!start) { setVorschlagFehler('Start-Adresse konnte nicht gefunden werden. Bitte genauer eingeben (Straße, PLZ, Ort).'); return; }
      }
      let ziel = null;
      if (zielEinbeziehen) {
        ziel = await adresseKoordinaten(zielAdresse.trim() || TOUR_START.query);
        if (!ziel) { setVorschlagFehler('Ziel-Adresse konnte nicht gefunden werden. Bitte genauer eingeben.'); return; }
      }
      const stoppObj = (tour.kunden || []).map(tk => ({
        id: tk.id, name: tk.kunde_name, lat: tk.kunde_lat, lng: tk.kunde_lng,
        uhrzeit: tk.uhrzeit, status: tk.status,
      }));
      const mitKo = stoppObj.filter(s => typeof s.lat === 'number' && typeof s.lng === 'number');
      if (mitKo.length < 1) {
        setVorschlagFehler('Kein Kunde dieser Tour hat Koordinaten — bitte in der Akte einen Karten-Pin setzen.');
        return;
      }
      // OSRM braucht mind. 2 Punkte. Ohne Start UND Ziel muessen es >=2 Kunden sein.
      if (!start && !ziel && mitKo.length < 2) {
        setVorschlagFehler('Bitte Start- oder Zieladresse aktivieren (bei nur einem Kunden ohne Start/Ziel gibt es keine Route).');
        return;
      }
      setStartPunkt(start); setZielPunkt(ziel);   // Karte exakt wie die Berechnung
      const r = await berechneTourVorschlag(stoppObj, {
        start, ziel, startzeit, dauerMin: Number(dauerMin) || 30, pufferMin: 10,
      });
      setVorschlag(r);
    } catch (e) {
      console.warn('Tour-Optimierung fehlgeschlagen', e);
      setVorschlagFehler('Das Routing (OSRM) ist gerade nicht erreichbar. Bitte gleich noch einmal versuchen.');
    } finally {
      setVorschlagLaedt(false);
    }
  };

  const vorschlagUebernehmen = async () => {
    if (!vorschlag) return;
    setAnwendenLaeuft(true);
    try {
      for (const s of vorschlag.plan) {
        // Setzt die vorgeschlagene Ankunftszeit + Status bestaetigt.
        // Rein interne Tour-Planung — es wird KEINE Mail an den Kunden ausgeloest.
        await onUpdateAssignment(s.id, { uhrzeit: s.ankunft, status: 'bestaetigt' });
      }
      setVorschlag(null);
    } finally {
      setAnwendenLaeuft(false);
    }
  };

  if (isLoading || !tour) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm py-12 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
      </div>
    );
  }

  const bestaetigt = (tour.kunden || []).filter(k => k.status === 'bestaetigt');
  const planung = (tour.kunden || []).filter(k => k.status !== 'bestaetigt');

  return (
    <div>
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 hover:text-gray-900 mb-3"
      >
        <ChevronLeft className="w-4 h-4" /> Zurück zur Übersicht
      </button>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              <CalendarIcon className="w-3.5 h-3.5" /> {formatDatum(tour.datum)}
            </div>
            <h2 className="text-2xl font-bold text-gray-800">{tour.name}</h2>
            <div className="text-sm text-gray-500 mt-1">
              {tour.anzahl_bestaetigt} bestätigt &middot; {tour.anzahl_geplant} in Planung
            </div>
            {(effektiv.gesamtKm > 0 || strasseLaedt) && (
              <div className="text-sm mt-1 flex items-center gap-1.5 flex-wrap" style={{ color: CI.brombeer }}>
                <RouteIcon className="w-4 h-4" />
                <span className="font-bold">Gesamtstrecke: {fmtKm(effektiv.gesamtKm)}</span>
                {effektiv.gesamtMin != null && (
                  <span>· Fahrzeit ~{fmtMin(effektiv.gesamtMin)}</span>
                )}
                <span className="text-xs text-gray-400">
                  ({effektiv.quelle === 'strasse' ? 'Straße, schnellster Weg' : 'Luftlinie'})
                </span>
                {strasseLaedt && (
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> berechne Fahrstrecke…
                  </span>
                )}
                {strasseFehler && (
                  <span className="text-xs text-amber-600">· Straßen-Routing gerade nicht erreichbar, zeige Luftlinie</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <RouteAufsHandyKnopf tourKunden={tour.kunden || []} />
            <button
              onClick={onShowKundeModal}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-bold shadow-sm"
              style={{ backgroundColor: CI.brombeer }}
            >
              <Plus className="w-4 h-4" /> Kunde hinzufügen
            </button>
            <button
              onClick={onEditTour}
              className="flex items-center gap-1 text-xs font-bold px-3 py-2 bg-white text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200"
              title="Name oder Datum aendern"
            >
              <Edit3 className="w-3.5 h-3.5" /> Bearbeiten
            </button>
            <button
              onClick={() => onDeleteTour(tour)}
              className="flex items-center gap-1 text-xs font-bold px-3 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-md border border-red-200"
              title="Tour löschen"
            >
              <Trash2 className="w-3.5 h-3.5" /> Tour löschen
            </button>
          </div>
        </div>
      </div>

      {/* Assistent: sinnvolle Reihenfolge vorschlagen (ab/zu Buero) */}
      <div className="bg-white rounded-xl shadow-sm border p-5 mb-5" style={{ borderColor: CI.orchidee }}>
        <div className="flex items-center gap-2 text-sm font-bold mb-3" style={{ color: CI.brombeer }}>
          <RouteIcon className="w-4 h-4" /> Assistent: Route optimieren
        </div>

        {/* Start-/Zieladresse: je mit Haekchen "in die Route aufnehmen" */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div className="text-xs text-gray-600">
            <label className="flex items-center gap-1.5 mb-1 font-semibold text-gray-700">
              <input type="checkbox" checked={startEinbeziehen} onChange={e => setStartEinbeziehen(e.target.checked)} />
              Startadresse in die Route aufnehmen
            </label>
            <input type="text" value={startAdresse} onChange={e => setStartAdresse(e.target.value)}
              placeholder="Straße, PLZ, Ort" disabled={!startEinbeziehen}
              className={`w-full border rounded-md px-2 py-1.5 text-sm ${startEinbeziehen ? 'border-gray-300' : 'border-gray-200 bg-gray-100 text-gray-400'}`} />
          </div>
          <div className="text-xs text-gray-600">
            <label className="flex items-center gap-1.5 mb-1 font-semibold text-gray-700">
              <input type="checkbox" checked={zielEinbeziehen} onChange={e => setZielEinbeziehen(e.target.checked)} />
              Zieladresse in die Route aufnehmen
            </label>
            <input type="text" value={zielAdresse} onChange={e => setZielAdresse(e.target.value)}
              placeholder="Straße, PLZ, Ort" disabled={!zielEinbeziehen}
              className={`w-full border rounded-md px-2 py-1.5 text-sm ${zielEinbeziehen ? 'border-gray-300' : 'border-gray-200 bg-gray-100 text-gray-400'}`} />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-600">
            Startzeit<br />
            <input type="time" value={startzeit} onChange={e => setStartzeit(e.target.value)}
              className="mt-0.5 border border-gray-300 rounded-md px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-gray-600">
            Dauer/Termin<br />
            <span className="flex items-center gap-1">
              <input type="number" min="5" step="5" value={dauerMin} onChange={e => setDauerMin(e.target.value)}
                className="mt-0.5 w-16 border border-gray-300 rounded-md px-2 py-1 text-sm" /> min
            </span>
          </label>
          <button onClick={optimieren} disabled={vorschlagLaedt}
            className="px-4 py-2 rounded-lg text-white font-bold text-sm disabled:opacity-50 flex items-center gap-2 ml-auto"
            style={{ backgroundColor: CI.brombeer }}>
            {vorschlagLaedt ? <Loader2 className="w-4 h-4 animate-spin" /> : <RouteIcon className="w-4 h-4" />}
            Reihenfolge vorschlagen
          </button>
        </div>

        {vorschlagFehler && <div className="mt-3 text-sm text-red-600">{vorschlagFehler}</div>}

        {vorschlag && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <div className="text-sm text-gray-600 mb-2">
              <span className="font-semibold" style={{ color: CI.brombeer }}>Vorschlag</span> — Start {startzeit}
              {vorschlag.startLabel ? ` ab ${vorschlag.startLabel}` : ' (ohne Startadresse)'} ·
              {' '}Gesamtstrecke {fmtKm(vorschlag.gesamtKm)} · Fahrzeit ~{fmtMin(vorschlag.gesamtFahrMin)}
              {vorschlag.zielLabel ? ` · Ankunft ${vorschlag.zielLabel} ~${vorschlag.ende}` : ` · fertig ~${vorschlag.ende}`}
            </div>
            {vorschlag.startLabel && (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-300 bg-gray-100 mb-1.5">
                <span className="w-6 h-6 shrink-0 rounded bg-gray-800 text-white text-[10px] font-bold flex items-center justify-center">S</span>
                <div className="flex-1 min-w-0 text-sm"><span className="font-semibold text-gray-800">Start</span> <span className="text-gray-500">{vorschlag.startLabel}</span></div>
                <div className="text-sm font-bold text-gray-700 shrink-0">ab {startzeit}</div>
              </div>
            )}
            <ol className="space-y-1.5">
              {vorschlag.plan.map((s, i) => (
                <li key={s.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${s.konflikt ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                  <span className="w-6 h-6 shrink-0 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ background: CI.brombeer }}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-800 truncate">{s.name || 'Unbekannt'}</div>
                    <div className="text-xs text-gray-500">Anfahrt {fmtKm(s.fahrtKm)} · {fmtMin(s.fahrtMin)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-gray-800">{s.ankunft}–{s.abfahrt}</div>
                    {s.konflikt && (
                      <div className="text-[11px] text-amber-700 flex items-center gap-1 justify-end">
                        <AlertCircle className="w-3 h-3" /> feste Uhrzeit {s.fixUhrzeit}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
            {vorschlag.zielLabel && (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-300 bg-gray-100 mt-1.5">
                <span className="w-6 h-6 shrink-0 rounded bg-gray-800 text-white text-[10px] font-bold flex items-center justify-center">Z</span>
                <div className="flex-1 min-w-0 text-sm">
                  <span className="font-semibold text-gray-800">Ziel</span> <span className="text-gray-500">{vorschlag.zielLabel}</span>
                  {vorschlag.rueckfahrtKm != null && (
                    <span className="text-xs text-gray-400"> · Anfahrt {fmtKm(vorschlag.rueckfahrtKm)} · {fmtMin(vorschlag.rueckfahrtMin)}</span>
                  )}
                </div>
                <div className="text-sm font-bold text-gray-700 shrink-0">~{vorschlag.ende}</div>
              </div>
            )}
            {vorschlag.ohneKoordinaten?.length > 0 && (
              <div className="mt-2 text-xs text-amber-700">
                {vorschlag.ohneKoordinaten.length} Kunde(n) ohne Koordinaten sind nicht eingeplant (in der Akte einen Karten-Pin setzen).
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <button onClick={vorschlagUebernehmen} disabled={anwendenLaeuft}
                className="px-4 py-2 rounded-lg text-white font-bold text-sm disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: CI.green }}>
                {anwendenLaeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Reihenfolge & Zeiten übernehmen
              </button>
              <button onClick={() => setVorschlag(null)} className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100">
                Verwerfen
              </button>
              <span className="text-xs text-gray-400">Setzt die Uhrzeiten intern — es geht keine Mail an Kunden.</span>
            </div>
          </div>
        )}
      </div>

      {/* Karte mit Polyline in Sortier-Reihenfolge + andere Kunden in der Naehe */}
      <TourRoutenKarte
        tourKunden={tour.kunden || []}
        alleKunden={alleKunden}
        onAddKunde={onAddKunde}
        startPunkt={startPunkt}
        zielPunkt={zielPunkt}
      />

      {/* Bestaetigte Kunden — oben, sortiert nach Uhrzeit */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-5">
        <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          <h3 className="text-sm font-bold text-emerald-900">Bestätigt ({bestaetigt.length})</h3>
        </div>
        {bestaetigt.length === 0 ? (
          <div className="px-5 py-6 text-sm text-gray-500 italic">Noch keine bestätigten Termine. Setze geplante Kunden auf „Bestätigt", sobald die Uhrzeit feststeht.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {bestaetigt.map(tk => (
              <ZuweisungZeile
                key={tk.id}
                tk={tk}
                distanz={distanzMap[tk.id]}
                onUpdate={onUpdateAssignment}
                onRemove={onRemoveAssignment}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Geplante Kunden — darunter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-700" />
          <h3 className="text-sm font-bold text-amber-900">In Planung ({planung.length})</h3>
        </div>
        {planung.length === 0 ? (
          <div className="px-5 py-6 text-sm text-gray-500 italic">Keine offenen Planungs-Einträge.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {planung.map(tk => (
              <ZuweisungZeile
                key={tk.id}
                tk={tk}
                distanz={distanzMap[tk.id]}
                onUpdate={onUpdateAssignment}
                onRemove={onRemoveAssignment}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Eine einzelne Kunden-Zeile innerhalb der Tour
// --------------------------------------------------------------------------
function ZuweisungZeile({ tk, distanz, onUpdate, onRemove }) {
  const [uhrzeit, setUhrzeit] = useState(tk.uhrzeit || '');

  // Wenn die Liste neu geladen wird (anderer tk), den lokalen Input syncen.
  useEffect(() => {
    setUhrzeit(tk.uhrzeit || '');
  }, [tk.id, tk.uhrzeit]);

  const istBestaetigt = tk.status === 'bestaetigt';
  const ortZeile = [tk.kunde_plz, tk.kunde_ort].filter(Boolean).join(' ');

  const vorherMin = fmtMin(distanz?.vorherMin);
  const nachherMin = fmtMin(distanz?.nachherMin);

  return (
    <li className="px-5 py-3 flex items-center justify-between gap-4 hover:bg-gray-50">
      <div className="flex-1 min-w-0">
        <a
          href={`?kunde=${tk.kunde_id}`}
          target="_blank"
          rel="noreferrer"
          className="block text-sm font-bold text-gray-800 truncate hover:text-fuchsia-700 hover:underline"
          title="Akte in neuem Tab öffnen"
        >
          {tk.kunde_name || 'Unbekannt'}
        </a>
        {ortZeile && <div className="text-xs text-gray-500 truncate">{ortZeile}</div>}
      </div>

      {/* Entfernung zum vorherigen / naechsten Stopp der Tour.
          Auf kleinen Screens ausgeblendet, damit die Zeile nicht bricht. */}
      <div className="hidden sm:flex items-center gap-3 shrink-0 text-[11px]">
        <div className="flex flex-col items-end leading-tight w-24" title="Entfernung zum vorherigen Stopp">
          <span className="flex items-center gap-1 text-gray-400"><ArrowUp className="w-3 h-3" /> vorher</span>
          <span className="font-semibold text-gray-700">
            {fmtKm(distanz?.vorherKm)}{vorherMin ? ` · ${vorherMin}` : ''}
          </span>
        </div>
        <div className="flex flex-col items-end leading-tight w-24" title="Entfernung zum nächsten Stopp">
          <span className="flex items-center gap-1 text-gray-400"><ArrowDown className="w-3 h-3" /> nachher</span>
          <span className="font-semibold text-gray-700">
            {fmtKm(distanz?.nachherKm)}{nachherMin ? ` · ${nachherMin}` : ''}
          </span>
        </div>
      </div>

      {istBestaetigt && (
        <div className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          <input
            type="time"
            value={uhrzeit}
            onChange={(e) => setUhrzeit(e.target.value)}
            onBlur={() => {
              if ((uhrzeit || '') !== (tk.uhrzeit || '')) {
                onUpdate(tk.id, { uhrzeit: uhrzeit || null });
              }
            }}
            className="px-2 py-1 border border-gray-300 rounded text-sm w-24 focus:ring-2 focus:ring-emerald-300"
          />
        </div>
      )}

      <button
        onClick={() => onUpdate(tk.id, { status: istBestaetigt ? 'planung' : 'bestaetigt' })}
        className={`text-xs font-bold px-3 py-1.5 rounded-md border transition-colors ${istBestaetigt
          ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
          : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'}`}
      >
        {istBestaetigt ? 'auf Planung zurück' : 'bestätigen'}
      </button>

      <button
        onClick={() => onRemove(tk.id, tk.kunde_name)}
        className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-md"
        title="Kunde von dieser Tour entfernen"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}

// --------------------------------------------------------------------------
// Modal: Tour anlegen ODER bearbeiten — gleiche Felder, je nach `modus`
// anderer Titel + Button-Text. initialName/initialDatum nur fuer Edit.
// --------------------------------------------------------------------------
function TourFormModal({ modus = 'neu', initialName = '', initialDatum = '', onClose, onSubmit }) {
  const [name, setName] = useState(initialName);
  const [datum, setDatum] = useState(initialDatum);
  const [busy, setBusy] = useState(false);
  const istEdit = modus === 'edit';

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !datum) return;
    setBusy(true);
    await onSubmit(name.trim(), datum);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-slate-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Plus className="w-5 h-5" style={{ color: CI.brombeer }} />
            {istEdit ? 'Tour bearbeiten' : 'Neue Tour anlegen'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name der Tour</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Bremen-Nord"
              autoFocus
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tagesdatum</label>
            <input
              type="date"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-300"
            />
            <p className="text-xs text-gray-500 mt-1">Keine Uhrzeit auf Tour-Ebene — Uhrzeiten kommen erst pro Kunde, wenn die Termine bestätigt werden.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-bold text-gray-600 hover:text-gray-900">Abbrechen</button>
            <button
              type="submit"
              disabled={!name.trim() || !datum || busy}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-bold disabled:opacity-50"
              style={{ backgroundColor: CI.brombeer }}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {istEdit ? 'Speichern' : 'Tour anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Modal: Kunde fuer eine Tour auswaehlen (Suche)
// --------------------------------------------------------------------------
function KundeAuswahlModal({ alleKunden, bereitsZugeordnet, onClose, onAdd }) {
  const [suche, setSuche] = useState('');

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    const verfuegbar = (alleKunden || []).filter(k => !bereitsZugeordnet.includes(k.id));
    if (!q) return verfuegbar.slice(0, 50);
    return verfuegbar.filter(k => {
      const t = `${k.firstName || ''} ${k.lastName || ''} ${k.zip || ''} ${k.city || ''} ${k.tour || ''}`.toLowerCase();
      return t.includes(q);
    }).slice(0, 50);
  }, [alleKunden, bereitsZugeordnet, suche]);

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden flex flex-col max-h-[80vh]">
        <div className="p-5 border-b border-gray-100 bg-slate-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-5 h-5" style={{ color: CI.brombeer }} /> Kunde zur Tour hinzufügen
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Name, PLZ, Ort oder bisheriger Tour-Eintrag…"
              autoFocus
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-300"
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">Nur Kunden, die noch nicht auf dieser Tour zugewiesen sind. Wenn ein Kunde bereits auf einer anderen aktiven Tour ist, weist das Backend mit einer Meldung ab.</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {gefiltert.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-gray-500">
              {suche ? 'Keine Treffer.' : 'Keine verfuegbaren Kunden gefunden.'}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {gefiltert.map(k => (
                <li key={k.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-800 truncate">
                      {[k.lastName, k.firstName].filter(Boolean).join(', ') || 'Unbenannt'}
                    </div>
                    <div className="text-xs text-gray-500 truncate flex items-center gap-2 flex-wrap">
                      {(k.zip || k.city) && <span>{[k.zip, k.city].filter(Boolean).join(' ')}</span>}
                      {k.tour && <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 text-[10px]">ClickUp-Tour: {k.tour}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => onAdd(k.id, 'planung')}
                      className="text-xs font-bold px-3 py-1.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                    >
                      + Planung
                    </button>
                    <button
                      onClick={() => onAdd(k.id, 'bestaetigt')}
                      className="text-xs font-bold px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                    >
                      + Bestätigt
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Knopf "Route aufs Handy senden" — baut einen Universal-Deep-Link
 * fuer Apple Maps / Google Maps und oeffnet ihn. Auf dem Mac landet
 * man im Browser, auf dem iPhone direkt in der Karten-App.
 *
 * Bewusst kein Aktiv-Versand (E-Mail/SMS): einfacher Link reicht,
 * Hendrik kann ihn ueber Continuity / Handoff aufs iPhone schicken
 * oder den QR-Code in den Browser-Devtools nutzen.
 */
function RouteAufsHandyKnopf({ tourKunden }) {
  const link = useMemo(() => baueRoutenLink(tourKunden), [tourKunden]);
  const anzahl = anzahlStoppsMitAdresse(tourKunden);
  const disabled = !link || anzahl < 1;
  return (
    <a
      href={link || '#'}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => { if (disabled) e.preventDefault(); }}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold shadow-sm border text-sm ${disabled ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50'}`}
      title={disabled
        ? 'Keine zugewiesenen Kunden mit Adresse — kein Link moeglich'
        : `Oeffnet Apple Maps / Google Maps mit ${anzahl} Stopps in Reihenfolge, ausgehend vom aktuellen Standort`}
    >
      <Smartphone className="w-4 h-4" /> Route aufs Handy
    </a>
  );
}

